import React, { useState } from 'react';
import { SystemSettings } from '../types';
import { Menu, X, Send, Instagram } from 'lucide-react';

interface AboutUsProps {
  onBack: () => void;
  onGetStarted: () => void;
  systemSettings: SystemSettings | null;
}

const AboutUs: React.FC<AboutUsProps> = ({ onBack, onGetStarted, systemSettings }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const siteName = systemSettings?.siteName || 'BTCC-AI Trading';

  return (
    <div className="min-h-screen bg-bg overflow-x-hidden selection:bg-primary/30 text-white font-sans grid-bg">
      {/* Dynamic Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/10 blur-[120px] rounded-full animate-pulse" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary/5 blur-[120px] rounded-full animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Futuristic Navbar */}
      <nav className="fixed top-0 left-0 w-full z-[100] px-6 md:px-16 py-8 flex items-center justify-between bg-bg/40 backdrop-blur-2xl border-b border-border">
        <div className="flex items-center gap-2 group cursor-pointer" onClick={onBack}>
          <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
            <span className="text-black font-black text-2xl">{siteName[0]}</span>
          </div>
          <span className="font-black text-2xl tracking-tighter text-white uppercase">{siteName}</span>
        </div>
        
        <div className="flex items-center gap-4 md:gap-8">
          <button onClick={onBack} className="hidden lg:block text-[10px] font-black uppercase tracking-[0.4em] text-white/30 hover:text-[#AEB877] transition-all">Back to Home</button>
          <button onClick={onGetStarted} className="primary-button hidden sm:block">Get Started</button>
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-white/60 hover:text-white transition-colors"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-[90] lg:hidden transition-all duration-500 ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-bg/95 backdrop-blur-2xl" onClick={() => setIsMenuOpen(false)} />
        <div className={`absolute top-0 right-0 w-full h-full max-w-sm bg-surface border-l border-border p-12 flex flex-col gap-12 transition-transform duration-500 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col gap-8 mt-24">
            <button 
              onClick={() => { onBack(); setIsMenuOpen(false); }} 
              className="text-left text-xs font-black uppercase tracking-[0.3em] text-white/40 hover:text-primary transition-all"
            >
              Back to Home
            </button>
            {[
              { label: 'Ecosystem', id: 'ecosystem' },
              { label: 'Protocol', id: 'protocol' },
              { label: 'Neural Core', id: 'neural-core' },
              { label: 'Security', id: 'security' }
            ].map((item) => (
              <button 
                key={item.id} 
                onClick={() => { onBack(); setIsMenuOpen(false); }}
                className="text-left text-xs font-black uppercase tracking-[0.3em] text-white/40 hover:text-primary transition-all"
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="mt-auto space-y-6">
            <button onClick={() => { onGetStarted(); setIsMenuOpen(false); }} className="w-full primary-button py-4 text-xs">Get Started</button>
            <div className="flex gap-8 justify-center pt-8 border-t border-white/5">
              <a href={systemSettings?.telegramLink || "#"} target="_blank" rel="noreferrer" className="text-white/20 hover:text-primary transition-all">
                <Send size={24} />
              </a>
              <a href={systemSettings?.instagramLink || "#"} target="_blank" rel="noreferrer" className="text-white/20 hover:text-primary transition-all">
                <Instagram size={24} />
              </a>
            </div>
          </div>
        </div>
      </div>

      <main className="relative pt-32 pb-24 px-6 z-10">
        <div className="max-w-4xl mx-auto space-y-24">
          {/* Header Section */}
          <div className="text-center space-y-8">
            <div className="inline-flex items-center gap-4 px-8 py-3 rounded-full border border-border bg-white/5 text-white/60 text-[10px] font-black uppercase tracking-[0.4em]">
               <div className="w-2 h-2 rounded-full bg-primary shadow-[0_0_10px_var(--color-primary-glow)] animate-pulse" />
               Established June 2011
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter uppercase leading-[0.9]">
              About <span className="text-transparent bg-clip-text bg-gradient-to-b from-white to-white/20">Us.</span>
            </h1>
            <p className="text-2xl font-black text-[#AEB877] uppercase tracking-tighter">15 Years of Innovation in Crypto & AI Trading</p>
            <p className="text-muted text-lg font-bold uppercase tracking-wider leading-relaxed">
              Founded in June 2011, during the early days of Bitcoin, BTCC has grown into one of the world’s longest-serving cryptocurrency trading platforms. From the beginning, our goal has been to combine advanced technology with secure trading infrastructure to make cryptocurrency trading more efficient and accessible.
            </p>
            <p className="text-muted text-lg font-bold uppercase tracking-wider leading-relaxed">
              Over the years, we have successfully navigated multiple market cycles while continuing to innovate. Today, BTCC is expanding the future of trading through Artificial Intelligence-powered trading solutions, designed to simplify the trading experience and help users take advantage of market opportunities automatically.
            </p>
            <p className="text-muted text-lg font-bold uppercase tracking-wider leading-relaxed">
              By combining AI technology, automated strategies, and reliable exchange infrastructure, we empower millions of traders worldwide to participate in the digital economy with confidence.
            </p>
            <p className="text-muted text-lg font-bold uppercase tracking-wider leading-relaxed">
              Our commitment to security, reliability, and technological innovation has earned the trust of traders across the globe.
            </p>
          </div>

          {/* Platform Achievements */}
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl font-black uppercase tracking-tighter">Platform Achievements</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { label: 'Security Breaches', value: '0' },
                { label: 'Bitcoin Halvings Witnessed', value: '4' },
                { label: 'Registered Users', value: '10.16M' }
              ].map((stat, i) => (
                <div key={i} className="glass-card p-10 text-center group hover:border-primary/50 transition-all">
                  <p className="text-4xl font-black mb-4 tracking-tighter group-hover:text-primary transition-colors">{stat.value}</p>
                  <p className="text-[10px] font-black text-white/20 uppercase tracking-[0.4em]">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Slogan, Vision, Mission */}
          <div className="space-y-16">
            <div className="glass-card p-12 space-y-6 border-l-4 border-l-primary">
              <h2 className="text-[10px] font-black text-primary uppercase tracking-[0.5em]">Our Slogan</h2>
              <p className="text-4xl font-black tracking-tighter uppercase">Ai for a Better Future</p>
              <p className="text-white/40 font-bold uppercase tracking-widest leading-relaxed">
                Our slogan represents our belief that technology—especially Artificial Intelligence and blockchain innovation—can create a smarter, more efficient future for financial trading worldwide.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="space-y-6">
                <h2 className="text-3xl font-black uppercase tracking-tighter">Our Vision</h2>
                <p className="text-muted text-sm font-bold uppercase tracking-widest leading-relaxed">
                  Our vision is to make AI-powered crypto trading reliable, intelligent, and accessible to everyone, regardless of their trading experience or location.
                </p>
                <p className="text-muted text-sm font-bold uppercase tracking-widest leading-relaxed">
                  We believe the future of financial markets will be driven by automation, machine learning, and data-driven strategies that empower users to trade smarter.
                </p>
              </div>
              <div className="space-y-6">
                <h2 className="text-3xl font-black uppercase tracking-tighter">Our Mission</h2>
                <p className="text-muted text-sm font-bold uppercase tracking-widest leading-relaxed">
                  Our mission is to build a fair, intelligent, and highly reliable trading ecosystem powered by cutting-edge technology.
                </p>
                <p className="text-muted text-sm font-bold uppercase tracking-widest leading-relaxed">
                  Through the development of AI trading robots, automated market analysis, and user-friendly platforms, we aim to simplify trading while maintaining the highest standards of security and transparency.
                </p>
                <p className="text-muted text-sm font-bold uppercase tracking-widest leading-relaxed">
                  By combining advanced AI algorithms with a stable trading infrastructure, we strive to create a platform where users can benefit from technology-driven trading strategies without requiring deep market expertise.
                </p>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-4xl font-black uppercase tracking-tighter">Our Core Values</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { title: 'Focus', desc: 'We focus on building intelligent trading systems that optimize every stage of the crypto trading process.' },
                { title: 'Innovation & Growth', desc: 'We embrace innovation, continuously improving our AI trading technology, automation tools, and platform performance to stay ahead in the evolving crypto market.' },
                { title: 'Trading Experience', desc: 'We continuously optimize our products and services to deliver a fast, seamless, and technology-driven trading experience powered by AI insights.' },
                { title: 'Fairness', desc: 'We believe that everyone should have access to advanced trading technology. Our platform provides fair, transparent, and equal opportunities for traders around the world.' }
              ].map((val, i) => (
                <div key={i} className="glass-card p-10 space-y-4">
                  <h3 className="text-xl font-black uppercase tracking-tighter text-[#AEB877]">{val.title}</h3>
                  <p className="text-white/40 text-xs font-bold uppercase tracking-widest leading-relaxed">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Info */}
          <div className="text-center pt-12 border-t border-white/10">
            <p className="text-[10px] font-black text-white/20 uppercase tracking-[0.4em] mb-4">Official Website</p>
            <a href="https://btcc.com" target="_blank" rel="noreferrer" className="text-2xl font-black hover:text-primary transition-colors">btcc.com</a>
          </div>
        </div>
      </main>

      {/* Global Footer */}
      <footer className="py-16 px-6 border-t border-border relative z-10 bg-bg">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-white/10 text-[9px] font-black uppercase tracking-[0.4em]">
            © 2026 {siteName.toUpperCase()} SYSTEMS. <br />
            ALL NEURAL PATHWAYS RESERVED.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
